import { Badge, Body, Box, Image, useTheme } from '@hover/blueprint';
import React from 'react';

import { BasicCard, CARD_PADDING } from './BasicCard';
import type { BasicCardProps } from './BasicCard';

export interface MaterialCardProps extends BasicCardProps {
  backgroundColor?: string;
  backgroundImage?: string;
  coverImageUrl?: string;
  coverImageAlt: string;
  header: string | React.ReactNode;
  subtitle?: string;
  count?: number;
}

export const MaterialCard = ({
  backgroundColor,
  backgroundImage,
  count,
  coverImageUrl,
  coverImageAlt,
  header,
  subtitle,
  sx,
  ...props
}: MaterialCardProps) => {
  const { colors, zIndices } = useTheme();
  const countToDisplay = count && count > 1 ? `+${count - 1}` : undefined;

  return (
    <BasicCard
      backgroundColorProp={backgroundColor}
      backgroundImage={backgroundImage}
      padding={0}
      position="relative"
      sx={{
        ...sx,
        aspectRatio: '1/1' || 'auto',
        width: '100%',
        minHeight: '240px',
        position: 'relative',
      }}
      {...props}
    >
      <Box flex="1">
        {coverImageUrl && (
          <Box
            maxHeight={600}
            ml={CARD_PADDING}
            mt={CARD_PADDING}
            position="relative"
          >
            <Image
              alt={coverImageAlt}
              borderColor="neutral.0"
              borderRadius={300}
              borderStyle="solid"
              borderWidth={400}
              height="100%"
              objectFit="cover"
              src={coverImageUrl}
              width={96}
            />
            {countToDisplay && (
              <Box
                backdropFilter="blur(25px)"
                borderRadius="200"
                bottom={200}
                left={200}
                overflow="hidden"
                paddingX={200}
                paddingY={100}
                position="absolute"
              >
                <Box
                  backgroundColor="neutral.700"
                  height="100%"
                  left={0}
                  opacity={0.4}
                  position="absolute"
                  top={0}
                  width="100%"
                />
                <Body
                  color="neutral.0"
                  m={0}
                  textStyle="bodyFootnote"
                  zIndex={zIndices.base}
                >
                  {countToDisplay}
                </Body>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Badge
        bottom={500}
        colorMode="dark"
        left={500}
        position="absolute"
        variant="glass"
      >
        <Body
          color={backgroundImage ? colors.neutral[0] : 'inherit'}
          fontWeight={700}
          isTruncated
          mb={0}
          mt="auto"
          size={300}
        >
          {header}
        </Body>
        <Body
          color={backgroundImage ? colors.neutral[0] : 'inherit'}
          isTruncated
          m={0}
          opacity={0.72}
          size={300}
        >
          {subtitle}
        </Body>
      </Badge>
    </BasicCard>
  );
};
